import { TextButton as Button, COLORS, DropdownFilter, LoadingIndicator, Size, SystemIcons, TypographyBase } from "@laerdal/life-react-components";
import axios from "axios";
import styled from "styled-components";
import { useEffect, useState } from "react";
import ProductApi from "../../../services/api/ProductApi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectMarket, selectTranslateWithAi } from "../../../store/slices/globalConfigSlice";
import { marketList } from "../../../model/constants/marketContants";
import { ProductRelevantArticlesDto } from "../../../model/dto/productRelevantArticlesDto";
import { ProductLineComponentsDto } from "../../../model/dto/productLineComponentsDto";
import VerticalTab from "../../../commonComponents/VerticalTab";

const VerticalTabsContainer = styled.div`
  display: flex;
  width: 280px;
  flex-direction: column;
`;

const ArticlesContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${COLORS.neutral_200};
  background: ${COLORS.white};
  padding: 32px;

  display: flex;
  flex-direction: column;
  gap 12px;
  width:100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const LoadingIndicatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
`;

const FaqLink = styled.a`
  &:link, &:visited, &:hover, &:active {
    color: ${COLORS.primary_600};
    text-decoration: none; 
  }

  color: ${COLORS.primary_600};
  font-size: 17px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px; 
`;

const Components = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const ViewAllButton = styled(Button)`
    align-self: end;
    span {
        display: flex;
        align-items: center;
    }
`;

interface ArticlesSectionProps {
    categoryLink?: string;
    productLink?: string;
    components: ProductLineComponentsDto[];
    isMediumScreen: boolean;
}

const ArticlesSection = ({ categoryLink, productLink, components, isMediumScreen }: ArticlesSectionProps) => {
    const { t } = useTranslation("landingPage");
    const navigate = useNavigate();

    const currentMartket = useSelector(selectMarket);
    const translateWithAi = useSelector(selectTranslateWithAi);

    const [componentLinkState, setComponentLinkState] = useState<string>('');
    const [loadingArticles, setLoadingArticles] = useState<boolean>(true);
    const [articlesDto, setArticlesDto] = useState<ProductRelevantArticlesDto | undefined>(undefined);

    useEffect(() => {
        if (components.length > 0) {
            setComponentLinkState(components[0].link);
        }
    }, [components]);

    useEffect(() => {
        if (!componentLinkState) return;

        const controller = new AbortController();
        const signal = controller.signal;
        setLoadingArticles(true);
        setArticlesDto(undefined);

        if (categoryLink && productLink && componentLinkState) {
            ProductApi
                .GetRelevantArticlesLine(categoryLink, productLink, componentLinkState, (categoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMartket].locale : undefined, signal)
                .then((data) => {
                    setLoadingArticles(false);
                    setArticlesDto(data);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                });
        }

        return () => {
            controller.abort();
        };
    }, [categoryLink, productLink, componentLinkState, translateWithAi, currentMartket]);

    if (components.length === 0) {
        return null;
    }

    return (
        <Components>
            <TypographyBase id='articles' $fontSize={32} $lineHeight={32} $fontWeight={700}>{t('Articles')}</TypographyBase>
            {!isMediumScreen &&
                <DropdownFilter
                    value={componentLinkState}
                    list={components.slice(0, 6).map((x) => ({
                        value: x.link,
                        displayLabel: x.name
                    }))}
                    onSelect={(value) => setComponentLinkState(value)}
                />
            }
            <Row>
                {isMediumScreen &&
                    <VerticalTabsContainer>
                        {components.slice(0, 6).map((x, index) =>
                            <VerticalTab
                                text={x.name}
                                isActive={x.link === componentLinkState}
                                onClick={() => setComponentLinkState(x.link)}
                                key={`vertical-tab-${index}`}
                            />
                        )}
                    </VerticalTabsContainer>
                }
                <ArticlesContainer>
                    {loadingArticles &&
                        <LoadingIndicatorContainer>
                            <LoadingIndicator />
                        </LoadingIndicatorContainer>
                    }
                    {!loadingArticles && articlesDto?.articles.slice(0, 8).map((item, index) =>
                        <FaqLink
                            onClick={(e) => {
                                e.preventDefault();
                                if (!!item.link)
                                    navigate(`/articles/Knowledge/${item.link}`);
                                else
                                    navigate(`/articles/${item.articleNumber}`);
                            }}
                            href={`/articles/Knowledge/${item.link}`}
                            key={`link-${index}`}>
                            {item.name}
                        </FaqLink>)
                    }
                </ArticlesContainer>
            </Row>
            <ViewAllButton
                variant="tertiary"
                size={Size.Small}
                onClick={() => navigate(`/category/${categoryLink}/product/${productLink}/articles/${componentLinkState}`)}>
                {t('View All')} <SystemIcons.ArrowLineRight />
            </ViewAllButton>
        </Components>
    );
};

export default ArticlesSection;
